/**
 * MAP mode, right footer: whether the mapping validates, and the one action that leaves the mode.
 *
 * It sits in the footer because the mapping list above it is 18 rows long: inline, Confirm was
 * below the fold at every column width the audit measured. The status line beside it answers the
 * only question the disabled button raises — "why can't I press this?".
 */

import type { EditorContext } from '../context';
import type { Controllers } from '../controllers/types';
import { button, icon } from './dom';
import { createPanel, note, runAsync, type PanelHandle } from './panel';

export function mountMappingFooterPanel(
  container: HTMLElement,
  ctx: EditorContext,
  controllers: Controllers,
): PanelHandle {
  const { store } = ctx;

  function render(root: HTMLElement): void {
    const validation = store.getState().mapping.validation;
    const errors = validation?.errors.length ?? 0;

    if (errors > 0) {
      root.appendChild(note(`${errors} error(s) — fix, then Validate`, 'note bad'));
    } else if (validation?.ok) {
      root.appendChild(note('Mapping is valid.', 'note ok'));
    } else {
      // Includes the "not ok, but no error listed" case: whatever it was, Validate has to run.
      root.appendChild(note('Not validated yet.'));
    }

    root.appendChild(
      button(
        [icon('check'), 'Confirm mapping → Animate'],
        () =>
          runAsync(controllers.mapping.confirmMapping(), () => {
            store.actions.setMode('animate');
          }),
        {
          class: 'primary',
          'data-action': 'confirm-mapping',
          disabled: !validation?.ok,
          title: validation?.ok ? 'Bind the rig and start animating' : 'Validate the mapping first',
        },
      ),
    );
  }

  return createPanel(container, ctx, render, {
    className: 'mapping-footer',
    slices: [(s) => s.mapping.validation],
  });
}
